import React, { useState, useEffect } from "react";
import {
  format,
  addDays,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
} from "date-fns";
import Calendar from "react-calendar";
import "react-calendar/dist/Calendar.css";
import "./date_picker.css";

/// Available types: day, range, month
function DatePicker(props) {
  const { cancel, confirm, type, ogStartDate, ogEndDate } = props;
  const [startDate, setStartDate] = useState(
    ogStartDate ? ogStartDate : new Date()
  );
  const [endDate, setEndDate] = useState(
    ogEndDate ? ogEndDate : addDays(new Date(), 1)
  );
  const [currentMonth, setCurrentMonth] = useState(startOfMonth(new Date()));

  useEffect(() => {
    if (type === "month") {
      // Update the start and end date when the month changes
      setStartDate(currentMonth);
      setEndDate(endOfMonth(currentMonth));
    }
  }, [currentMonth, type]);

  const handleStartDateChange = (date) => {
    setStartDate(date);
    if (type === "day") {
      setEndDate(date);
    }
  };

  const handleEndDateChange = (date) => {
    setEndDate(date);
  };

  const formatDate = (date) => {
    if (type === "month") {
      // Format for 'Month' view: Month, Year (Show only once)
      return format(currentMonth, "MMM, yyyy");
    } else {
      // Default format for 'Week' and 'Day' views
      if (date.getDay() < 10) {
        return format(date, "MMM d, yyyy");
      } else {
        return format(date, "MMM dd, yyyy");
      }
    }
  };

  const tileClassName = ({ date, view }) => {
    if (type === "month" && view === "month") {
      // Highlight the entire month in 'Month' view
      const monthStart = startOfMonth(startDate);
      const monthEnd = endOfMonth(startDate);
      if (date >= monthStart && date <= monthEnd) {
        return "highlight";
      }
    } else if (type === "week" && view === "month") {
      // Highlight the entire week in 'Week' view (month view)
      const weekStart = startOfWeek(startDate);
      const weekEnd = endOfWeek(startDate);
      if (date >= weekStart && date <= weekEnd) {
        return "highlight";
      }
    }

    // Customize the class for Saturday and Sunday dates
    if (date.getDay() === 0) {
      return "sunday";
    } else if (date.getDay() === 6) {
      return "saturday";
    }

    if (view === "month") {
      // Hide days from other months in 'Month' view
      const monthStart = startOfMonth(startDate);
      const monthEnd = endOfMonth(startDate);
      if (date < monthStart || date > monthEnd) {
        return "hide";
      }
    }

    return "";
  };

  const confirmDate = () => {
    confirm(startDate, endDate);
  };

  const cancelDate = () => {
    setStartDate(new Date());
    setEndDate(addDays(new Date(), 1));
    cancel();
  };

  const customLocale = {
    weekStartsOn: 0,
    weekdaysShort: ["Sa", "Su", "Mo", "Tu", "We", "Th", "Fr"],
  };

  const isDateDisabled = (date) => {
    return type === "month";
  };

  return (
    <div className="date-picker-container">
      <div className="left-section">
        <div className="selected-dates">
          <p>Date selected:</p>
          <div className="date-box">
            <p>{formatDate(startDate)}</p>
          </div>
          {type !== "day" && type !== "month" && <hr className="line"></hr>}
          {type !== "day" && type !== "month" && (
            <div className="date-box">
              <p>{formatDate(endDate)}</p>
            </div>
          )}
        </div>
      </div>
      <div className="calendar-container">
        <Calendar
          value={type !== "day" ? [startDate, endDate] : startDate}
          onChange={(date) => {
            if (type !== "day") {
              handleStartDateChange(date[0]);
              handleEndDateChange(date[1]);
            } else {
              handleStartDateChange(date);
            }
          }}
          onActiveStartDateChange={({ activeStartDate }) =>
            setCurrentMonth(startOfMonth(activeStartDate))
          }
          selectRange={type !== "day"}
          minDetail={"month"}
          tileClassName={tileClassName}
          formatShortWeekday={(locale, date) => {
            return customLocale.weekdaysShort[date.getDay()];
          }}
          locales={customLocale}
          showNeighboringMonth={false}
          prevLabel="&#x3c;"
          nextLabel="&#x3e;"
          showNavigation={true}
          showDoubleView={false}
          tileDisabled={({ date }) => isDateDisabled(date)}
        />
        <div className="button-container">
          <button className="cancel-button" onClick={cancelDate}>
            Cancel
          </button>
          <button className="confirm-button" onClick={confirmDate}>
            Confirm
          </button>
        </div>
      </div>
    </div>
  );
}

export default DatePicker;
